'use client';

import React, { useState } from 'react';

type Stage = 'zahir' | 'sunnat' | 'haqq' | 'amanah' | 'uboor';

interface ConcentricRingProps {
  activeStage: Stage;
  onStageSelect: (stage: Stage) => void;
  exampleName?: string;
  highlightedNames?: string[];
}

const STAGES: { id: Stage; label: string; arabic: string; ring: number; color: string; description: string }[] = [
  { id: 'zahir', label: 'ZAHIR', arabic: 'ظَاهِر', ring: 0, color: '#ffffff', description: 'What is apparent' },
  { id: 'sunnat', label: 'SUNNAT ALLAH', arabic: 'سُنَّةُ اللَّه', ring: 1, color: '#e0e0e0', description: 'The patterns/processes' },
  { id: 'haqq', label: 'HAQQ', arabic: 'حَقّ', ring: 2, color: '#c0c0c0', description: 'The truth/reality' },
  { id: 'amanah', label: 'AMANAH', arabic: 'أَمَانَة', ring: 3, color: '#a0a0a0', description: 'The trust' },
  { id: 'uboor', label: 'UBOOR', arabic: 'عُبُور', ring: 4, color: '#808080', description: 'The bridge/crossing' },
];

const RING_RADII = [52, 90, 128, 166, 200];
const CENTER = 220;

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function arcPath(cx: number, cy: number, r: number, startAngle: number, endAngle: number) {
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArc = endAngle - startAngle <= 180 ? '0' : '1';
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y}`;
}

function ringSegmentPath(cx: number, cy: number, innerR: number, outerR: number, startAngle: number, endAngle: number) {
  const outerStart = polarToCartesian(cx, cy, outerR, startAngle);
  const outerEnd = polarToCartesian(cx, cy, outerR, endAngle);
  const innerStart = polarToCartesian(cx, cy, innerR, endAngle);
  const innerEnd = polarToCartesian(cx, cy, innerR, startAngle);
  const largeArc = endAngle - startAngle > 180 ? '1' : '0';
  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${outerR} ${outerR} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerStart.x} ${innerStart.y}`,
    `A ${innerR} ${innerR} 0 ${largeArc} 0 ${innerEnd.x} ${innerEnd.y}`,
    'Z'
  ].join(' ');
}

export default function ConcentricRing({ activeStage, onStageSelect, exampleName, highlightedNames = [] }: ConcentricRingProps) {
  const [hoveredStage, setHoveredStage] = useState<Stage | null>(null);

  const stageOrder: Stage[] = ['zahir', 'sunnat', 'haqq', 'amanah', 'uboor'];
  const activeIndex = stageOrder.indexOf(activeStage);

  // Outer decorative star points
  const starPoints = Array.from({ length: 8 }, (_, i) => {
    const angle = (i * 45) - 90;
    const outerR = 218;
    const innerR = 208;
    const p1 = polarToCartesian(CENTER, CENTER, outerR, angle);
    const p2 = polarToCartesian(CENTER, CENTER, innerR, angle + 22.5);
    const p3 = polarToCartesian(CENTER, CENTER, outerR, angle + 45);
    return `${p1.x},${p1.y} ${p2.x},${p2.y} ${p3.x},${p3.y}`;
  });

  // SLEDOA arc segments on ring 1 (sunnat ring)
  const sledoaLabels = ['S', 'L', 'E', 'D', 'O', 'A'];
  const sledoaSegmentAngle = 360 / 6;

  // Names of Allah nodes on ring 2 (haqq ring)
  const namesNodes = [
    { arabic: 'الرَّزَّاق', name: 'Ar-Razzaq', id: 'razzaq' },
    { arabic: 'الْمُحْيِي', name: 'Al-Muhyi', id: 'muhyi' },
    { arabic: 'الْحَكِيم', name: 'Al-Hakim', id: 'hakeem' },
    { arabic: 'اللَّطِيف', name: 'Al-Latif', id: 'latif' },
    { arabic: 'الْخَالِق', name: 'Al-Khaliq', id: 'khaliq' },
    { arabic: 'الْوَهَّاب', name: 'Al-Wahhab', id: 'wahhab' },
    { arabic: 'الْمَتِين', name: 'Al-Mateen', id: 'mateen' },
    { arabic: 'الْمُعِيد', name: 'Al-Mu\'id', id: 'muid' },
  ];

  return (
    <div className="flex flex-col items-center gap-4">
      <style>{`
        @keyframes nameNodePulse {
          0%, 100% { filter: drop-shadow(0 0 3px rgba(201,168,76,0.3)); }
          50% { filter: drop-shadow(0 0 10px rgba(201,168,76,0.9)); }
        }
        .name-node-highlighted {
          animation: nameNodePulse 1.5s ease-in-out infinite;
        }
      `}</style>
      <svg
        viewBox={`0 0 ${CENTER * 2} ${CENTER * 2}`}
        className="w-full max-w-[440px] mx-auto"
        style={{ filter: 'drop-shadow(0 0 20px rgba(255,255,255,0.05))' }}
      >
        {/* Outer decorative frame */}
        <circle cx={CENTER} cy={CENTER} r={215} fill="none" stroke="white" strokeWidth="0.5" opacity="0.2" />
        <circle cx={CENTER} cy={CENTER} r={210} fill="none" stroke="white" strokeWidth="0.3" opacity="0.15" />

        {/* Star points */}
        {starPoints.map((pts, i) => (
          <polygon key={i} points={pts} fill="none" stroke="white" strokeWidth="0.5" opacity="0.3" />
        ))}

        {/* Geometric grid lines */}
        {Array.from({ length: 8 }, (_, i) => {
          const angle = (i * 45 - 90) * Math.PI / 180;
          const x2 = CENTER + 215 * Math.cos(angle);
          const y2 = CENTER + 215 * Math.sin(angle);
          return <line key={i} x1={CENTER} y1={CENTER} x2={x2} y2={y2} stroke="white" strokeWidth="0.3" opacity="0.08" />;
        })}

        {/* RING 4 — UBOOR / AMANAH (outermost interactive) */}
        {stageOrder.map((stage, si) => {
          if (si < 1) return null;
          const innerR = RING_RADII[si - 1] + 4;
          const outerR = RING_RADII[si] - 2;
          const isActive = stage === activeStage;
          const isPast = si <= activeIndex;
          const isHovered = stage === hoveredStage;

          return (
            <g key={stage}>
              <circle
                cx={CENTER} cy={CENTER} r={(innerR + outerR) / 2}
                fill="none"
                stroke={isActive ? 'white' : isPast ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.1)'}
                strokeWidth={outerR - innerR - 2}
                strokeDasharray={isActive ? 'none' : '4 2'}
                style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}
                onClick={() => onStageSelect(stage)}
                onMouseEnter={() => setHoveredStage(stage)}
                onMouseLeave={() => setHoveredStage(null)}
              />
              {/* Ring label */}
              <text
                x={CENTER}
                y={CENTER - (innerR + outerR) / 2 + 4}
                textAnchor="middle"
                fill={isActive ? 'black' : 'rgba(255,255,255,0.5)'}
                fontSize="7"
                fontFamily="monospace"
                letterSpacing="2"
                style={{ pointerEvents: 'none' }}
              >
                {STAGES[si].label}
              </text>
            </g>
          );
        })}

        {/* SLEDOA segments on ring 1 */}
        {sledoaLabels.map((letter, i) => {
          const startAngle = i * sledoaSegmentAngle - 90;
          const endAngle = startAngle + sledoaSegmentAngle - 2;
          const midAngle = startAngle + sledoaSegmentAngle / 2;
          const isActive = activeStage === 'sunnat';
          const midPt = polarToCartesian(CENTER, CENTER, (RING_RADII[0] + RING_RADII[1]) / 2, midAngle + 90);

          return (
            <g key={letter}>
              <path
                d={ringSegmentPath(CENTER, CENTER, RING_RADII[0] + 4, RING_RADII[1] - 2, startAngle, endAngle)}
                fill={isActive ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.03)'}
                stroke={isActive ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.15)'}
                strokeWidth="0.5"
                style={{ cursor: 'pointer', transition: 'all 0.3s' }}
                onClick={() => onStageSelect('sunnat')}
              />
              <text
                x={midPt.x} y={midPt.y + 3}
                textAnchor="middle"
                fill={isActive ? 'white' : 'rgba(255,255,255,0.4)'}
                fontSize="9"
                fontFamily="monospace"
                fontWeight="bold"
                style={{ pointerEvents: 'none' }}
              >
                {letter}
              </text>
            </g>
          );
        })}

        {/* Names of Allah nodes on ring 2 — with highlightedNames pulse */}
        {namesNodes.map((node, i) => {
          const angle = (i * (360 / namesNodes.length)) - 90;
          const r = (RING_RADII[1] + RING_RADII[2]) / 2;
          const pt = polarToCartesian(CENTER, CENTER, r, angle + 90);
          const isActive = activeStage === 'haqq' || activeStage === 'amanah' || activeStage === 'uboor';
          const isHighlighted = highlightedNames.includes(node.id) || highlightedNames.includes(node.name.toLowerCase().replace('al-', '').replace('ar-', ''));

          return (
            <g key={node.name} className={isHighlighted ? 'name-node-highlighted' : ''}>
              <circle
                cx={pt.x} cy={pt.y} r={isHighlighted ? 12 : 10}
                fill={isHighlighted ? 'rgba(201,168,76,0.25)' : isActive ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.03)'}
                stroke={isHighlighted ? 'rgba(201,168,76,0.9)' : isActive ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.15)'}
                strokeWidth={isHighlighted ? 1.5 : 0.5}
                style={{ cursor: 'pointer', transition: 'all 0.3s' }}
                onClick={() => onStageSelect('haqq')}
              />
              <text
                x={pt.x} y={pt.y + 3}
                textAnchor="middle"
                fill={isHighlighted ? 'rgba(201,168,76,1)' : isActive ? 'white' : 'rgba(255,255,255,0.3)'}
                fontSize="5"
                fontFamily="serif"
                style={{ pointerEvents: 'none' }}
              >
                {node.arabic}
              </text>
            </g>
          );
        })}

        {/* CORE — ZAHIR */}
        <circle
          cx={CENTER} cy={CENTER} r={RING_RADII[0] - 2}
          fill={activeStage === 'zahir' ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.04)'}
          stroke={activeStage === 'zahir' ? 'white' : 'rgba(255,255,255,0.3)'}
          strokeWidth="1"
          style={{ cursor: 'pointer', transition: 'all 0.3s' }}
          onClick={() => onStageSelect('zahir')}
        />

        {/* Core label */}
        <text x={CENTER} y={CENTER - 12} textAnchor="middle" fill="white" fontSize="8" fontFamily="monospace" letterSpacing="3" opacity="0.9">
          ZAHIR
        </text>
        <text x={CENTER} y={CENTER + 2} textAnchor="middle" fill="white" fontSize="14" fontFamily="serif" opacity="0.8">
          ظَاهِر
        </text>
        {exampleName && (
          <text x={CENTER} y={CENTER + 18} textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="6" fontFamily="monospace" letterSpacing="1">
            {exampleName.toUpperCase().slice(0, 16)}
          </text>
        )}

        {/* AYA symbol */}
        <text x={CENTER} y={CENTER + 30} textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="8" fontFamily="serif">
          آيَة
        </text>

        {/* Progress indicator dots */}
        {stageOrder.map((stage, i) => {
          const angle = (i * 72) - 90;
          const pt = polarToCartesian(CENTER, CENTER, 205, angle);
          const isActive = stage === activeStage;
          const isPast = i < activeIndex;
          return (
            <circle
              key={stage}
              cx={pt.x} cy={pt.y} r={isActive ? 4 : 2.5}
              fill={isActive ? 'white' : isPast ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.15)'}
              style={{ cursor: 'pointer', transition: 'all 0.3s' }}
              onClick={() => onStageSelect(stage)}
            />
          );
        })}
      </svg>

      {/* Stage selector buttons */}
      <div className="flex gap-1 flex-wrap justify-center">
        {STAGES.map((stage) => (
          <button
            key={stage.id}
            onClick={() => onStageSelect(stage.id)}
            className={`px-3 py-1.5 font-mono text-[10px] tracking-widest uppercase border transition-all duration-200 ${
              activeStage === stage.id
                ? 'bg-white text-black border-white' :'bg-transparent text-white/50 border-white/20 hover:border-white/50 hover:text-white/80'
            }`}
          >
            <span className="hidden sm:inline">{stage.arabic} · </span>{stage.label}
          </button>
        ))}
      </div>
    </div>
  );
}
